import React, { useEffect, useState } from "react";
import { FaTrash, FaPlus, FaEdit } from "react-icons/fa";
import ModifierDetail from "../ModifierDetail/ModifierDetail";
import CreateModifierModal from "./components/CreateModifierModal/CreateModifierModal";
import DeleteModifierModal from "./components/DeleteModifierModal/DeleteModifierModal";
import "./Table.scss";
import { Button } from "react-bootstrap";

interface SelectionModifierType {
  readonly modifier_id: number;
  modifier_name: string;
  modifier_description?: string;
  local_id: number;
  items_affected: Array<{
    item_id: number;
    item_name: string;
  }>;
  options_included: Array<{
    option_id: number;
    option_name: string;
  }>;
}

interface TableProps {
  data: SelectionModifierType[];
  setData: (data: SelectionModifierType[]) => void;
}

const Table: React.FC<TableProps> = ({ data, setData }) => {
  const [currentData, setCurrentData] = useState<SelectionModifierType[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedModifier, setSelectedModifier] = useState<SelectionModifierType | null>(null);

  useEffect(() => {
    setCurrentData(data);
  }, [data]);

  const handleCreateModifier = () => {
    setShowCreateModal(true);
  };

  const handleEditModifier = (modifier: SelectionModifierType) => {
    setSelectedModifier(modifier);
    setShowEditModal(true);
  };

  const handleDeleteModifier = (modifier: SelectionModifierType) => {
    setSelectedModifier(modifier);
    setShowDeleteModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedModifier(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedModifier(null);
  };

  if (currentData.length === 0) {
    return (
      <div className="selection-modifiers-table">
        <div className="modifiers-grid">
          <div className="empty-state">
            <div className="empty-content">
              <h3>No hay grupos modificadores</h3>
              <p>Crea tu primer grupo modificador para empezar a personalizar las opciones de tus productos.</p>
              <button className="create-button" onClick={handleCreateModifier}>
                <FaPlus />
                Crear Primer Grupo
              </button>
            </div>
          </div>
        </div>

        {showCreateModal && (
          <CreateModifierModal
            show={showCreateModal}
            handleClose={() => setShowCreateModal(false)}
            modifiers={data}
            setModifiers={setData}
          />
        )}
      </div>
    );
  }
  
  return (
    <div className="selection-modifiers-table">
      <Button className="create-button" onClick={handleCreateModifier} variant="none">
                <FaPlus />
                Crear Grupo
      </Button>
      <div className="modifiers-grid">
        {currentData.map((modifier) => (
          <div
            key={modifier.modifier_id}
            className="selection-group-card menu-box like-menu"
          >
            <div className="card-top-row">
              <h2 className="group-title">{modifier.modifier_name}</h2>
              <div className="card-actions">
                <button
                  className="icon-btn"
                  onClick={(e) => { e.stopPropagation(); handleEditModifier(modifier); }}
                  title="Editar"
                >
                  <FaEdit />
                </button>
                <button
                  className="icon-btn danger"
                  onClick={(e) => { e.stopPropagation(); handleDeleteModifier(modifier); }}
                  title="Eliminar"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
            {modifier.modifier_description?.trim() && (
              <p className="group-desc">{modifier.modifier_description}</p>
            )}
            <div className="group-meta">
              <div className="meta-block">
                <span className="meta-label">Vinculado a</span>
                <div className="chips">
                  {modifier.items_affected && modifier.items_affected.length > 0 ? (
                    <>
                      {modifier.items_affected.slice(0, 7).map((item) => (
                        <span key={item.item_id} className="chip chip-blue" data-full={item.item_name}>
                          {item.item_name}
                        </span>
                      ))}
                      {modifier.items_affected.length > 7 && (
                        <span className="chip chip-more">+{modifier.items_affected.length - 7}</span>
                      )}
                    </>
                  ) : (
                    <span className="chip chip-empty">Ninguno</span>
                  )}
                </div>
              </div>
              <div className="meta-block">
                <span className="meta-label">Opciones del grupo</span>
                <div className="chips">
                  {modifier.options_included && modifier.options_included.length > 0 ? (
                    <>
                      {modifier.options_included.slice(0, 7).map((option) => (
                        <span key={option.option_id} className="chip chip-orange" data-full={option.option_name}>
                          {option.option_name}
                        </span>
                      ))}
                      {modifier.options_included.length > 7 && (
                        <span className="chip chip-more">+{modifier.options_included.length - 7}</span>
                      )}
                    </>
                  ) : (
                    <span className="chip chip-empty">Vacío</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showCreateModal && (
        <CreateModifierModal
          show={showCreateModal}
          handleClose={() => setShowCreateModal(false)}
          modifiers={data}
          setModifiers={setData}
        />
      )}

      {showEditModal && selectedModifier && (
        <ModifierDetail 
          show={showEditModal} 
          setShow={handleCloseEditModal} 
          mod={selectedModifier}
          data={data}
          setData={setData}
        />
      )}

      {showDeleteModal && selectedModifier && (
        <DeleteModifierModal
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          mod={selectedModifier}
          setModifiers={setData}
          modifiers={data}
        />
      )}
    </div>
  );
};

export default Table;
